import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VoyageConfigPanel = ({ onConfigChange, onOptimize, isOptimizing }) => {
  const [config, setConfig] = useState({
    departurePort: '',
    arrivalPort: '',
    vesselId: '',
    cargoType: '',
    cargoQuantity: '',
    charterType: '',
    departureDate: '',
    arrivalDate: '',
    maxSpeed: '14',
    fuelType: 'vlsfo'
  });

  const vesselOptions = [
    { value: 'mv-atlantic-voyager', label: 'MV Atlantic Voyager (Bulk Carrier)' },
    { value: 'mv-pacific-star', label: 'MV Pacific Star (Container)' },
    { value: 'mv-nordic-wind', label: 'MV Nordic Wind (Tanker)' },
    { value: 'mv-southern-cross', label: 'MV Southern Cross (Bulk Carrier)' }
  ];

  const portOptions = [
    { value: 'rotterdam', label: 'Rotterdam, Netherlands' },
    { value: 'hamburg', label: 'Hamburg, Germany' },
    { value: 'antwerp', label: 'Antwerp, Belgium' },
    { value: 'singapore', label: 'Singapore' },
    { value: 'shanghai', label: 'Shanghai, China' },
    { value: 'los-angeles', label: 'Los Angeles, USA' },
    { value: 'santos', label: 'Santos, Brazil' },
    { value: 'durban', label: 'Durban, South Africa' }
  ];

  const cargoTypeOptions = [
    { value: 'iron-ore', label: 'Iron Ore' },
    { value: 'coal', label: 'Coal' },
    { value: 'grain', label: 'Grain' },
    { value: 'containers', label: 'Containers' },
    { value: 'crude-oil', label: 'Crude Oil' },
    { value: 'lng', label: 'LNG' },
    { value: 'general-cargo', label: 'General Cargo' }
  ];

  const charterTypeOptions = [
    { value: 'voyage', label: 'Voyage Charter' },
    { value: 'time', label: 'Time Charter' },
    { value: 'bareboat', label: 'Bareboat Charter' },
    { value: 'coa', label: 'Contract of Affreightment' }
  ];

  const fuelTypeOptions = [
    { value: 'vlsfo', label: 'VLSFO (0.5% Sulfur)' },
    { value: 'mgo', label: 'Marine Gas Oil' },
    { value: 'lng', label: 'Liquefied Natural Gas' },
    { value: 'methanol', label: 'Green Methanol' },
    { value: 'ammonia', label: 'Green Ammonia' }
  ];

  const handleInputChange = (field, value) => {
    const updatedConfig = { ...config, [field]: value };
    setConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  const handleOptimizeClick = () => {
    onOptimize(config);
  };

  const isConfigValid = config?.departurePort && config?.arrivalPort && config?.vesselId && config?.departureDate;

  return (
    <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Route" size={20} className="text-primary" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Voyage Configuration</h2>
              <p className="text-sm text-muted-foreground">Configure voyage parameters for optimization</p>
            </div>
          </div>
          <Button
            onClick={handleOptimizeClick}
            disabled={!isConfigValid || isOptimizing}
            loading={isOptimizing}
            iconName="Zap"
            iconPosition="left"
            className="bg-primary hover:bg-primary/90"
          >
            Optimize Route
          </Button>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Route Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="MapPin" size={16} strokeWidth={2} />
            <span>Route Details</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Departure Port"
              placeholder="Select departure port"
              options={portOptions}
              value={config?.departurePort}
              onChange={(value) => handleInputChange('departurePort', value)}
              searchable
              required
            />
            
            <Select
              label="Arrival Port"
              placeholder="Select arrival port"
              options={portOptions}
              value={config?.arrivalPort}
              onChange={(value) => handleInputChange('arrivalPort', value)}
              searchable
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Departure Date"
              type="datetime-local"
              value={config?.departureDate}
              onChange={(e) => handleInputChange('departureDate', e?.target?.value)}
              required
            />
            
            <Input
              label="Expected Arrival Date"
              type="datetime-local"
              value={config?.arrivalDate}
              onChange={(e) => handleInputChange('arrivalDate', e?.target?.value)}
            />
          </div>
        </div>

        {/* Vessel & Cargo Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Ship" size={16} strokeWidth={2} />
            <span>Vessel & Cargo</span>
          </h3>
          
          <Select
            label="Vessel"
            placeholder="Select vessel"
            options={vesselOptions}
            value={config?.vesselId}
            onChange={(value) => handleInputChange('vesselId', value)}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Cargo Type"
              placeholder="Select cargo type"
              options={cargoTypeOptions}
              value={config?.cargoType}
              onChange={(value) => handleInputChange('cargoType', value)}
            />
            
            <Input
              label="Cargo Quantity (MT)"
              type="number"
              placeholder="Enter cargo quantity"
              value={config?.cargoQuantity}
              onChange={(e) => handleInputChange('cargoQuantity', e?.target?.value)}
            />
          </div>

          <Select
            label="Charter Type"
            placeholder="Select charter type"
            options={charterTypeOptions}
            value={config?.charterType}
            onChange={(value) => handleInputChange('charterType', value)}
          />
        </div>

        {/* Performance Parameters */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Settings" size={16} strokeWidth={2} />
            <span>Performance Parameters</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Maximum Speed (knots)"
              type="number"
              min="8"
              max="20"
              step="0.5"
              value={config?.maxSpeed}
              onChange={(e) => handleInputChange('maxSpeed', e?.target?.value)}
            />
            
            <Select
              label="Fuel Type"
              placeholder="Select fuel type"
              options={fuelTypeOptions}
              value={config?.fuelType}
              onChange={(value) => handleInputChange('fuelType', value)}
            />
          </div>
        </div>

        {/* Configuration Summary */}
        {isConfigValid && (
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-foreground mb-2">Configuration Summary</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-muted-foreground">Route:</div>
              <div className="text-foreground font-medium">
                {portOptions?.find(p => p?.value === config?.departurePort)?.label?.split(',')?.[0]} → {portOptions?.find(p => p?.value === config?.arrivalPort)?.label?.split(',')?.[0]}
              </div>
              <div className="text-muted-foreground">Vessel:</div>
              <div className="text-foreground font-medium">
                {vesselOptions?.find(v => v?.value === config?.vesselId)?.label?.split(' (')?.[0]}
              </div>
              <div className="text-muted-foreground">Departure:</div>
              <div className="text-foreground font-medium">
                {config?.departureDate ? new Date(config.departureDate)?.toLocaleDateString() : 'Not set'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoyageConfigPanel;